import { Box, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { type ChartsItemContentProps, ChartsTooltip, PieChart } from '@mui/x-charts';
import { useDrawingArea } from '@mui/x-charts/hooks';
import type { ElementType, FC } from 'react';

type ChartData = { value: number; label: string }[];
interface AvaniHygienePieChartProps {
  data: ChartData;
  pass?: boolean;
}

const StyledTitle = styled('text')(({ theme }) => ({
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: 24
}));

const PieCenterLabel: FC<AvaniHygienePieChartProps> = ({ data, pass }) => {
  const { width, height, left, top } = useDrawingArea();

  return (
    <g>
      <StyledTitle x={left + width / 2} y={top + height / 2 + 3}>
        {!pass ? data[0].label : data[1].label}
      </StyledTitle>
    </g>
  );
};

const TooltipComponent = (inc: any) => {
  const theme = useTheme();
  const data: ChartData = inc.series && (inc?.series?.data as ChartData[]);

  return (
    <Box
      sx={{
        borderRadius: '16px',
        backgroundColor: 'white',
        padding: '16px'
      }}
    >
      {data.map((item) => {
        return (
          <Box
            key={item.label}
            sx={{
              display: 'flex',
              minWidth: '160px'
            }}
          >
            <Typography
              sx={{
                flexGrow: 1,
                color: item.label === 'Fail' ? theme.palette.aMagenta.main : theme.palette.aTeal.main
              }}
            >
              {item.label}
            </Typography>
            <Typography>{item.value}</Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export const AvaniHygienePieChart: FC<AvaniHygienePieChartProps> = ({ data, pass }) => {
  const theme = useTheme();
  const { width, height, left, top } = useDrawingArea();

  const chartColors = [theme.palette.aMagenta.main, theme.palette.aTeal.main];
  const chartLeftOffset = '-47px';

  return (
    <Box
      sx={{
        padding: 0,
        width: '125px',
        overflow: 'hidden',
        margin: '0 auto'
      }}
    >
      <Box
        sx={{
          padding: 0,
          margin: 0,
          overflow: 'hidden',
          marginLeft: chartLeftOffset
        }}
      >
        <PieChart
          series={[{ data: data, innerRadius: 40 }]}
          height={120}
          width={320}
          margin={{
            top: 0,
            left: 0
          }}
          colors={chartColors}
          sx={{
            ['.MuiPieArc-root']: { stroke: 'none' },
            ['text']: {
              fill: pass ? chartColors[1] : chartColors[0]
            }
          }}
          tooltip={{ trigger: 'none' }}
          slotProps={{
            legend: { hidden: true }
          }}
        >
          <PieCenterLabel data={data} pass={pass}></PieCenterLabel>
          <ChartsTooltip
            trigger={'item'}
            slots={{
              itemContent: TooltipComponent as unknown as ElementType<ChartsItemContentProps<any>>
            }}
          />
        </PieChart>
      </Box>
    </Box>
  );
};
